/**
 * THE CUT TICKETS — LAW 59 (18 October 2026). OWNER: app-architect (the type),
 * manufacturing-ops-lead (the document).
 *
 * The owner: "build the cut tickets into pauls backend so he could do this on
 * ipad … make it so paul can write using apple pen and sketch if needed. he
 * also sends 3 photos to tailor".
 *
 * WHY THIS IS IN CORE, like `CutSheet` beside it: a ticket is a document a
 * cutter in Thailand staples to a bolt and cuts cloth from. A server object and
 * a hand-written web interface kept in step by hand is how a column goes
 * missing from a printed ticket without anything failing; declared once here,
 * the server projects into it, the web renders from it, and both tests type
 * their fixtures against the same shape (the LAW 58 `LeadReference` precedent).
 *
 * A ticket is NEVER STORED. It is projected from the frozen cut sheet each time
 * it is read (apps/server/src/domain/cut-tickets.ts), so it cannot drift from
 * the record it prints. The one thing it carries that the sheet does not is
 * the tailor's own hand, the sketch, kept as strokes and appended.
 */

/* ───────────────────────────────────────────────────────────── the words */

/**
 * A word on a ticket, the house's way (P-THAI-01): the English rules.json
 * prints, the Thai where a named person signed it, and otherwise the glossary's
 * own sentence saying why there is none. A word the glossary has never heard
 * of (a figure such as "4 cm", a code) carries neither Thai nor marker.
 */
export interface BilingualWord {
  readonly termKey: string;
  readonly en: string;
  readonly th: string | null;
  readonly marker: string | null;
}

/** The ticket's standing words, by short key. The server sends every one; a missing one is a compile error on both sides. */
export const TICKET_WORD_KEYS = [
  'order',
  'client',
  'measurements',
  'body',
  'finished',
  'make',
  'cloth',
  'metres',
  'sketch',
  'staple',
  'photographs',
  'photographsNote',
] as const;

export type TicketWordKey = (typeof TICKET_WORD_KEYS)[number];

/* ──────────────────────────────────────────────────────────── the sketch */

/** One point of the pencil: x and y in 0..1 of the pad, and the pressure in 0..1. */
export type SketchPoint = readonly [number, number, number];

/** One stroke: its width in pad units, and its points. */
export interface SketchStroke {
  readonly w: number;
  readonly p: readonly SketchPoint[];
}

export interface SketchDrawing {
  /** The pad's size in CSS pixels when drawn, so the aspect is kept on paper. */
  readonly width: number;
  readonly height: number;
  readonly strokes: readonly SketchStroke[];
}

export interface TicketSketch extends SketchDrawing {
  readonly sketchId: string;
  readonly seq: number;
  readonly drawnByName: string;
  readonly drawnAt: string;
}

/**
 * What a drawing may be, in one place: the server refuses outside these, the
 * route caps the body at `bytes`, and the pad clamps a stroke's width to the
 * bounds and takes no more points once the drawing is at the point cap or the
 * byte cap read as points, saying so on its status line. A drawing is small by
 * nature.
 */
export const SKETCH_LIMITS = {
  bytes: 256 * 1024,
  strokes: 2000,
  points: 40_000,
  sideMin: 100,
  sideMax: 4000,
  widthMin: 0.1,
  widthMax: 64,
} as const;

/* ─────────────────────────────────────────────────────────── the ticket */

export interface TicketMeasurement {
  readonly id: string;
  readonly label: BilingualWord;
  /** Exactly as block 2 of the sheet holds it: a number, an enum id, or null. The web words an enum id from the catalogue. */
  readonly value: number | string | null;
  /** Body plus ease, what the cutter drafts to; null where the ease set does not cover the field. */
  readonly finished: number | null;
  readonly unit: string | null;
}

export interface TicketDecision {
  readonly optionId: string;
  readonly label: BilingualWord;
  readonly value: BilingualWord;
}

export interface TicketCloth {
  readonly mill: string;
  readonly millRef: string;
  readonly name: string;
  readonly colourName: string;
  readonly colourSrgbHex: string;
  readonly weave: string;
  readonly pattern: string;
  readonly weightGsm: number | null;
  /** This garment's own terms of the house cloth table, summed; null where the table has none for it. */
  readonly metresForGarment: number | null;
  readonly metresStatement: string;
}

export interface CutTicket {
  readonly garment: string;
  readonly index: number;
  readonly count: number;
  readonly garmentWord: BilingualWord;
  readonly measurements: readonly TicketMeasurement[];
  readonly decisions: readonly TicketDecision[];
  /** The tailor's making notes off the record, joined; null where there are none. */
  readonly notes: string | null;
  readonly cloth: TicketCloth;
  readonly sketch: TicketSketch | null;
}

export interface CutTicketsPayload {
  readonly orderId: string;
  readonly orderRef: string;
  readonly clientName: string;
  readonly placedAt: string;
  readonly commissionTypeLabel: string;
  readonly words: Readonly<Record<TicketWordKey, BilingualWord>>;
  readonly tickets: readonly CutTicket[];
  /** The words the glossary knows on these tickets, and how many are signed; the legend prints while they differ. */
  readonly wordCount: number;
  readonly signedCount: number;
  /** The same count as the server's own sentence. Rendered, never re-derived. */
  readonly statement: string;
}

/**
 * `API-ATELIER-TICKETS-GET`: the tickets and the governing photograph set that
 * prints with them, read apart from the order so an order's own screen does
 * not carry every drawing. The photograph set's shape is the photograph
 * module's (`photographSetPayload`), typed by the reader.
 */
export interface CutTicketsResponse<Photographs = unknown> {
  readonly orderId: string;
  readonly orderRef: string;
  readonly tickets: CutTicketsPayload | null;
  readonly photographs: Photographs | null;
}
